import { useEffect, useRef } from "react";
import type { SoundfontSource, SoundfontEntry } from "@midi-fantom-utils/player-core";
import type { MidiPortSelection, SoundOutput } from "../config.js";
import { SoundFontSettings } from "./SoundFontSettings.js";
import { MidiOutputSettings } from "./MidiOutputSettings.js";
import { TimelineAppearance } from "./TimelineAppearance.js";

type SettingsModalProps = {
  open: boolean;
  source: SoundfontSource;
  onSourceChange: (next: SoundfontSource) => void;
  soundOutput: SoundOutput;
  onSoundOutputChange: (next: SoundOutput) => void;
  midiPort: MidiPortSelection | null;
  onMidiPortChange: (next: MidiPortSelection) => void;
  catalog: ReadonlyArray<SoundfontEntry>;
  timelineHeight: number;
  onTimelineHeightChange: (next: number) => void;
  onClose: () => void;
};

export const SettingsModal = ({
  open,
  source,
  onSourceChange,
  soundOutput,
  onSoundOutputChange,
  midiPort,
  onMidiPortChange,
  catalog,
  timelineHeight,
  onTimelineHeightChange,
  onClose,
}: SettingsModalProps) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="settings-modal__backdrop"
      onMouseDown={onBackdropMouseDown}
      role="presentation"
    >
      <div
        className="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
        ref={dialogRef}
      >
        <header className="settings-modal__head">
          <h2 id="settings-modal-title" className="settings-modal__title">
            Настройки
          </h2>
          <button
            type="button"
            className="btn btn--ghost settings-modal__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </header>
        <div className="settings-modal__body">
          <div className="settings-section">
            <p className="muted">Выход звука</p>
            <label className="settings-row">
              <input
                type="radio"
                name="settings-output"
                value="sf2"
                checked={soundOutput === "sf2"}
                onChange={() => onSoundOutputChange("sf2")}
              />
              <span className="settings-row__label">SF2 (браузерный синтез)</span>
            </label>
            <label className="settings-row">
              <input
                type="radio"
                name="settings-output"
                value="midi"
                checked={soundOutput === "midi"}
                onChange={() => onSoundOutputChange("midi")}
              />
              <span className="settings-row__label">
                MIDI-устройство / GM-синтезатор ОС
              </span>
            </label>
          </div>
          {soundOutput === "sf2" ? (
            <SoundFontSettings
              source={source}
              onSourceChange={onSourceChange}
              catalog={catalog}
            />
          ) : (
            <MidiOutputSettings value={midiPort} onChange={onMidiPortChange} />
          )}
          <TimelineAppearance
            value={timelineHeight}
            onChange={onTimelineHeightChange}
          />
        </div>
        <footer className="settings-modal__foot">
          <button type="button" className="btn" onClick={onClose}>
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
};
